function hasValues(values) {
  return Array.isArray(values) && values.length > 0;
}

/**
 * Number of filter groups that currently have a selection.
 */
function activeFilterCount(filters) {
  return Object.values(filters).filter(hasValues).length;
}

function emptyPlatformFilters() {
  return {
    hostTypes: [],
    providers: [],
    releases: [],
    reports: [],
  };
}

function emptyDataHostFilters() {
  return {
    auditStatuses: [],
  };
}

function emptyNotificationFilters() {
  return {
    platformOrDataHost: [],
    releases: [],
    types: [],
    sources: [],
    reports: [],
  };
}

function notificationTargetName(item) {
  return item.sushi_service?.platform?.name || item.data_host?.name || null;
}

function filterPlatforms(platforms, filters) {
  const { hostTypes, providers, releases, reports } = filters;
  const hasHostTypes = hasValues(hostTypes);
  const hasProviders = hasValues(providers);
  const hasReleases = hasValues(releases);
  const hasReports = hasValues(reports);

  if (!hasHostTypes && !hasProviders && !hasReleases && !hasReports) {
    return platforms;
  }

  return platforms.filter((item) => {
    if (hasHostTypes) {
      const names = new Set((item.host_types || []).map((type) => type.name));
      if (!hostTypes.some((name) => names.has(name))) {
        return false;
      }
    }

    if (hasProviders && !providers.includes(item.content_provider_name)) {
      return false;
    }

    const reportsByRelease = item.counterReports || {};

    if (hasReleases) {
      if (!releases.some((release) => release in reportsByRelease)) {
        return false;
      }
    }

    if (hasReports) {
      // When a release is also selected, reports must belong to one of those
      // releases — not merely exist somewhere else on the same platform.
      const pool = hasReleases
        ? releases.flatMap((release) => reportsByRelease[release] || [])
        : Object.values(reportsByRelease).flat();
      const reportSet = new Set(pool);
      if (!reports.some((report) => reportSet.has(report))) {
        return false;
      }
    }

    return true;
  });
}

function filterDataHosts(hosts, filters) {
  const { auditStatuses } = filters;
  if (!hasValues(auditStatuses)) {
    return hosts;
  }
  return hosts.filter((item) => auditStatuses.includes(item.audit_status));
}

function filterNotifications(notifications, filters) {
  const { platformOrDataHost, releases, types, sources, reports } = filters;
  const hasTargets = hasValues(platformOrDataHost);
  const hasReleases = hasValues(releases);
  const hasTypes = hasValues(types);
  const hasSources = hasValues(sources);
  const hasReports = hasValues(reports);

  if (!hasTargets && !hasReleases && !hasTypes && !hasSources && !hasReports) {
    return notifications;
  }

  return notifications.filter((item) => {
    if (hasTargets) {
      const name = notificationTargetName(item);
      if (!name || !platformOrDataHost.includes(name)) {
        return false;
      }
    }

    if (
      hasReleases &&
      !(
        item.sushi_service &&
        releases.includes(item.sushi_service.counter_release)
      )
    ) {
      return false;
    }

    if (hasTypes && !types.includes(item.type)) {
      return false;
    }

    if (hasSources && !sources.includes(item.source)) {
      return false;
    }

    if (hasReports) {
      const reportIds = new Set(
        (item.reports || []).map((report) => report.report_id)
      );
      if (!reports.some((report) => reportIds.has(report))) {
        return false;
      }
    }

    return true;
  });
}

export {
  activeFilterCount,
  emptyDataHostFilters,
  emptyNotificationFilters,
  emptyPlatformFilters,
  filterDataHosts,
  filterNotifications,
  filterPlatforms,
  notificationTargetName,
};
